/**
 * Staff authentication guard for /admin/store/:slug routes.
 */

import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { useStaffAuthStore } from '../stores/staffAuthStore'

function extractStoreSlug(to: RouteLocationNormalized): string | undefined {
  return to.params.slug as string | undefined
}

function extractBootstrapToken(to: RouteLocationNormalized): string | null {
  const token = to.query.staff_token
  if (typeof token === 'string' && token.trim().length > 0) {
    return token.trim()
  }
  return null
}

export async function staffAuthGuard(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
): Promise<void> {
  const staffAuth = useStaffAuthStore()
  const errorRouteName = 'AdminAuthError'
  const routeSlug = extractStoreSlug(to)
  const bootstrapToken = extractBootstrapToken(to)

  if (bootstrapToken) {
    const tokenError = await staffAuth.initialize(bootstrapToken)
    if (tokenError) {
      staffAuth.reset()
      next({
        name: errorRouteName,
        query: { error: tokenError },
      })
      return
    }

    const cleanedQuery = { ...to.query }
    delete cleanedQuery.staff_token
    next({
      path: to.path,
      query: cleanedQuery,
      replace: true,
    })
    return
  }

  if (!staffAuth.isInitialized) {
    const error = await staffAuth.initialize()
    if (error) {
      next({
        name: errorRouteName,
        query: { error },
      })
      return
    }
  }

  if (!staffAuth.isAuthenticated) {
    next({
      name: errorRouteName,
      query: { error: 'NO_SESSION' },
    })
    return
  }

  if (routeSlug && !staffAuth.canAccessStore(routeSlug)) {
    next({
      name: errorRouteName,
      query: { error: 'STORE_MISMATCH' },
    })
    return
  }

  const requiresPharmacy = to.matched.some((record) => record.meta?.requiresPharmacy === true)
  if (requiresPharmacy && !staffAuth.isPharmacyStore) {
    const redirectSlug = routeSlug ?? staffAuth.currentSlug
    if (!redirectSlug) {
      next({
        name: errorRouteName,
        query: { error: 'STORE_MISMATCH' },
      })
      return
    }

    next({
      name: 'AdminDeliveries',
      params: { slug: redirectSlug },
      replace: true,
    })
    return
  }

  const requiredPermissions = to.meta.staffPermissions as string[] | undefined
  if (requiredPermissions?.length) {
    const hasPermission = requiredPermissions.some((permission) =>
      staffAuth.hasPermission(permission)
    )
    if (!hasPermission) {
      next({
        name: errorRouteName,
        query: { error: 'PERMISSION_DENIED' },
      })
      return
    }
  }

  next()
}

export async function requireStaffAuth(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
): Promise<void> {
  return staffAuthGuard(to, from, next)
}

export default staffAuthGuard
